import { useContext, useState , useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { postRequest } from '../../lib/api';
import toast from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Checkbox,
  Container,
  FormControl,
  FormControlLabel,
  IconButton,
  InputAdornment,
  InputLabel,
  OutlinedInput,
  Paper,
  Stack,
  TextField,
  Typography,
  CircularProgress,
} from '@mui/material';
import {
  Visibility,
  VisibilityOff,
  PersonOutline,
  LockOutlined
} from '@mui/icons-material';
import { AuthContext } from '../../authContext/context';
import { Link } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import { UserStatus, canLogin } from '../../constants/auth.constants';

export default function LoginPage() {
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [savedUsername, setSavedUsername] = useState('');
  const [savedPassword, setSavedPassword] = useState('');

  const navigate = useNavigate();
  const authCtx = useContext(AuthContext);

  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors },
  } = useForm();

  useEffect(() => {
    const remembered = localStorage.getItem('rememberMe');
    const username = localStorage.getItem('rememberedUsername');
    const password = localStorage.getItem('rememberedPassword');
    if (remembered === 'true' && username && password) {
      setRememberMe(true);
      setSavedUsername(username);
      setSavedPassword(password);
      setValue('username', username);
      setValue('password', password);
    }
  }, [setValue]);

  const onSubmit = async (data) => {
    setLoading(true);
    try {
      // Save credentials if rememberMe is checked
      if (rememberMe) {
        localStorage.setItem('rememberMe', 'true');
        localStorage.setItem('rememberedUsername', data.username);
        localStorage.setItem('rememberedPassword', data.password);
      } else {
        localStorage.removeItem('rememberMe');
        localStorage.removeItem('rememberedUsername');
        localStorage.removeItem('rememberedPassword');
      }

      const response = await postRequest('auth/login', data);

      // Handle OTP screen redirect
      if (response?.page?.OTP_SCREEN) {
        localStorage.setItem('username', response?.data?.username);
        localStorage.setItem('email', response?.data?.email);
        localStorage.setItem('id', response?.data?.userId);
        navigate('/otp', {
          state: {
            userId: response?.data?.userId,
            email: response?.data?.email,
            username: response?.data?.username
          }
        });
        return;
      }

      // Extract user data from response
      const userData = response?.data || {};
      // Support both new role field and legacy isSuperAdmin flag
      const userRole = userData.role || (userData.isSuperAdmin ? 'SUPERADMIN' : 'USER');
      const userStatus = userData.status || UserStatus.ACTIVE;

      // Check if user status allows login
      // if (userStatus && !canLogin(userStatus)) {
      //   toast.error(`Cannot login. Account status: ${userStatus}`);
      //   setLoading(false);
      //   return;
      // }

      // Check email verification (skip for SUPERADMIN)
      if (userRole !== 'SUPERADMIN' && !userData.isEmailVerified && userStatus !== UserStatus.PENDING) {
        toast.error('Please verify your email before logging in');
        setLoading(false);
        return;
      }

      // Store basic user info
      localStorage.setItem('username', userData.username);
      localStorage.setItem('email', userData.email);
      localStorage.setItem('id', userData.userId || userData._id);

      console.log('Login successful:', {
        role: userRole,
        status: userStatus,
        permissions: userData.permissions
      });

      // Login to auth context
      authCtx.Login(response?.token?.accessToken, {
        user: {
          userId: userData.userId || userData._id,
          email: userData.email,
          username: userData.username,
          userGroup: userData.userGroup,
          role: userRole,
          status: userStatus,
          firstName: userData.firstName,
          lastName: userData.lastName,
          hierarchyLevel: userData.hierarchyLevel,
          parentId: userData.parentId,
          createdBy: userData.createdBy
        },
        permissions: userData.permissions,
        role: userRole,
        status: userStatus
      });

      // Check if first time login or needs password change
      // Skip password change if isChangePassword is 1 (password already set)
      console.log('🔍 Frontend - isChangePassword:', userData.isChangePassword);
      console.log('🔍 Frontend - firstTimeLogin:', userData.firstTimeLogin);
      console.log('🔍 Frontend - typeof isChangePassword:', typeof userData.isChangePassword);

      const needsPasswordChange = (userData.firstTimeLogin === true ||
                                   userData.isChangePassword === 0) &&
                                   userData.isChangePassword !== 1;

      console.log('🔍 Frontend - needsPasswordChange:', needsPasswordChange);

      if (needsPasswordChange) {
        toast.success('Please change your password to continue');
        setTimeout(() => {
          navigate('/account/manage', {
            state: {
              email: userData.email,
              username: userData.username,
              needsPasswordChange: true
            }
          });
        }, 1500);
      } else {
        // Normal redirect based on role (no password change needed)
        const redirectPath = getRedirectPathByRole(userRole);
        setTimeout(() => {
          navigate(redirectPath, {
            state: {
              email: userData.email,
              username: userData.username
            }
          });
        }, 1000);
      }

    } catch (err) {
      console.error('Login error:', err);
      // Error already handled by axios interceptor
    } finally {
      setLoading(false);
    }
  };

  // Helper function to determine redirect path based on role
  const getRedirectPathByRole = (role) => {
    switch (role) {
      case 'SUPERADMIN':
        return '/dashboard';
      case 'PARTNER':
        return '/partners/dashboard';
      case 'PARTNER_USER':
        return '/customers';
      case 'SOLICITOR':
        return '/dashboard';
      case 'USER':
      default:
        return '/customers';
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        position: 'relative',
        overflow: 'hidden',
        '&::before': {
          content: '""',
          position: 'absolute',
          width: '150%',
          height: '150%',
          background: 'radial-gradient(circle, rgba(255,255,255,0.1) 1px, transparent 1px)',
          backgroundSize: '50px 50px',
          animation: 'moveBackground 20s linear infinite',
        },
        '@keyframes moveBackground': {
          '0%': { transform: 'translate(0, 0)' },
          '100%': { transform: 'translate(50px, 50px)' },
        },
      }}
    >
      <Container component="main" maxWidth="sm">
        <Box
          sx={{
            position: 'relative',
            zIndex: 1,
          }}
        >
          <Paper
            elevation={24}
            sx={{
              p: { xs: 3, sm: 5 },
              borderRadius: 4,
              background: 'rgba(255, 255, 255, 0.95)',
              backdropFilter: 'blur(20px)',
              boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
              border: '1px solid rgba(255, 255, 255, 0.18)',
              animation: 'fadeInUp 0.6s ease-out',
              '@keyframes fadeInUp': {
                '0%': {
                  opacity: 0,
                  transform: 'translateY(30px)',
                },
                '100%': {
                  opacity: 1,
                  transform: 'translateY(0)',
                },
              },
            }}
          >
            {/* Logo Section */}
            <Box sx={{ textAlign: 'center', mb: 4 }}>
              <Typography
                variant="h3"
                sx={{
                  fontWeight: 800,
                  fontSize: { xs: '2rem', sm: '2.5rem' },
                  background: 'linear-gradient(135deg, #764ba2 0%, #667eea 100%)',
                  backgroundClip: 'text',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  mb: 2,
                  fontFamily: 'Poppins',
                  letterSpacing: '0.05em',
                  animation: 'fadeInDown 0.8s ease-out',
                  '@keyframes fadeInDown': {
                    '0%': {
                      opacity: 0,
                      transform: 'translateY(-20px)',
                    },
                    '100%': {
                      opacity: 1,
                      transform: 'translateY(0)',
                    },
                  },
                }}
              >
                UNIVERSAL
              </Typography>
            
              <Typography variant="body1" color="text.secondary">
                Log in to your account
              </Typography>
            </Box>

            <Box component="form" onSubmit={handleSubmit(onSubmit)} noValidate>
              {/* Username Field */}
              <TextField
                label="Username"
                fullWidth
                margin="normal"
                type="text"
                {...register('username', {
                  required: 'Username is required',
                })}
                error={!!errors.username}
                helperText={errors.username?.message}
                disabled={loading}
                slotProps={{
                  input: {
                    startAdornment: (
                      <InputAdornment position="start">
                        <PersonOutline sx={{ color: '#764ba2' }} />
                      </InputAdornment>
                    ),
                  },
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      boxShadow: '0 4px 12px rgba(118, 75, 162, 0.15)',
                    },
                    '&.Mui-focused': {
                      boxShadow: '0 4px 12px rgba(118, 75, 162, 0.25)',
                    },
                  },
                  '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#764ba2',
                    borderWidth: '2px',
                  },
                  '& .MuiInputLabel-root.Mui-focused': {
                    color: '#764ba2',
                  },
                }}
              />

              {/* Password Field with Toggle */}
              <FormControl fullWidth margin="normal" variant="outlined">
                <InputLabel
                  htmlFor="password"
                  sx={{
                    '&.Mui-focused': {
                      color: '#764ba2',
                    },
                  }}
                >
                  Password
                </InputLabel>
                <OutlinedInput
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  {...register('password', {
                    required: 'Password is required',
                    minLength: {
                      value: 6,
                      message: 'Minimum 6 characters required',
                    },
                  })}
                  error={!!errors.password}
                  startAdornment={
                    <InputAdornment position="start">
                      <LockOutlined sx={{ color: '#764ba2' }} />
                    </InputAdornment>
                  }
                  endAdornment={
                    <InputAdornment position="end">
                      <IconButton
                        edge="end"
                        onClick={() => setShowPassword((prev) => !prev)}
                        disabled={loading}
                        sx={{
                          color: '#764ba2',
                          '&:hover': {
                            backgroundColor: 'rgba(118, 75, 162, 0.08)',
                          },
                        }}
                      >
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  }
                  label="Password"
                  disabled={loading}
                  sx={{
                    borderRadius: 2,
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      boxShadow: '0 4px 12px rgba(118, 75, 162, 0.15)',
                    },
                    '&.Mui-focused': {
                      boxShadow: '0 4px 12px rgba(118, 75, 162, 0.25)',
                    },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#764ba2',
                      borderWidth: '2px',
                    },
                  }}
                />
                {errors.password && (
                  <Typography
                    color="error"
                    variant="caption"
                    sx={{ mt: 0.5, ml: 1.5 }}
                  >
                    {errors.password.message}
                  </Typography>
                )}
              </FormControl>

              {/* Remember Me & Forgot */}
              <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
                mt={2}
                mb={3}
              >
                <FormControlLabel
                  control={
                    <Checkbox
                      disabled={loading}
                      checked={rememberMe}
                      onChange={(e) => setRememberMe(e.target.checked)}
                      sx={{
                        color: '#764ba2',
                        '&.Mui-checked': {
                          color: '#764ba2',
                        },
                      }}
                    />
                  }
                  label={
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      Remember me
                    </Typography>
                  }
                />
                <Link
                  to="/forgot-password"
                  component={RouterLink}
                  underline="hover"
                  variant="body2"
                  sx={{
                    color: '#764ba2',
                    fontWeight: 600,
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      color: '#667eea',
                    },
                  }}
                >
                  Forgot password?
                </Link>
              </Stack>

              {/* Submit */}
              <Button
                type="submit"
                variant="contained"
                fullWidth
                disabled={loading}
                sx={{
                  py: 1.8,
                  borderRadius: 2,
                  fontSize: '1rem',
                  fontWeight: 700,
                  textTransform: 'none',
                  background: 'linear-gradient(135deg, #764ba2 0%, #667eea 100%)',
                  boxShadow: '0 4px 15px rgba(118, 75, 162, 0.4)',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    background: 'linear-gradient(135deg, #764ba2 20%, #667eea 100%)',
                    boxShadow: '0 6px 20px rgba(118, 75, 162, 0.5)',
                    transform: 'translateY(-2px)',
                  },
                  '&:active': {
                    transform: 'translateY(0)',
                  },
                  '&.Mui-disabled': {
                    background: 'linear-gradient(135deg, #764ba2 0%, #667eea 100%)',
                    opacity: 0.6,
                  },
                }}
                startIcon={loading && <CircularProgress size={20} sx={{ color: 'white' }} />}
              >
                {loading ? 'Logging in...' : 'Login'}
              </Button>
            </Box>

           
          </Paper>
        </Box>
      </Container>
    </Box>
  );
}
